import {
  NumberInput as ChakraNumberInput,
  Field,
  InputGroup,
  InputGroupProps,
  NumberInputInputProps,
} from '@chakra-ui/react'
import {ComponentProps} from 'react'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {useFieldContext} from '../../integrations/form/index.js'
import {isDefined, multipleDigitsNumberFormatter} from '../../utils/index.js'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'
import {locale} from '../../integrations/tolgee/instance.js'

interface FieldProps extends ComponentProps<typeof ChakraNumberInput.Root> {
  label: string
  hideLabel?: boolean
  description?: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  helpText?: string
  //! create custom input fields for other types
  type?: 'text' | 'email' | 'tel' | 'url'
  bgColor?: string
  inputProps?: NumberInputInputProps
  inputGroupProps?: Omit<InputGroupProps, 'children'>
  hideControls?: boolean
  errorMessagePadding?: number
  /**
   * Message to show below the input when the field itself is valid, so a composite
   * control (e.g. an inline unit select) can surface its own error here.
   */
  errorMessage?: string
  /** Emit `null` when the input is cleared, instead of coercing to `min`/`defaultValue`/0. */
  allowEmpty?: boolean
  /**
   * Round what is typed to a multiple of `step` (default). Turn it off for a measured value, such as a
   * lab result, that must be kept exactly as entered; `step` then only drives the arrow buttons.
   */
  roundToStep?: boolean
}

function decimalPlaces(n: number): number {
  const s = n.toString()
  const dot = s.indexOf('.')
  return dot === -1 ? 0 : s.length - dot - 1
}

function snapToStep(value: number, step: number | undefined, min: number | undefined): number {
  if (!isDefined(step) || step <= 0) return value

  const base = isDefined(min) && !Number.isNaN(min) ? min : 0
  const steps = Math.round((value - base) / step)
  const precision = Math.max(decimalPlaces(step), decimalPlaces(base))

  return Number((base + steps * step).toFixed(precision))
}

export function NumberInput({
  required,
  hideLabel,
  description,
  inputProps,
  inputGroupProps,
  hideControls,
  allowEmpty,
  errorMessage,
  roundToStep = true,
  ...props
}: FieldProps) {
  const field = useFieldContext<number | null>()

  return (
    <FieldRoot required={required} disabled={props.disabled} invalid={Boolean(errorMessage)}>
      <Field.Label srOnly={hideLabel}>
        {props.label}
        {required && <FieldRequiredIndicator />}
      </Field.Label>

      <ChakraNumberInput.Root
        width="100%"
        backgroundColor="bg"
        name={field.name}
        formatOptions={{
          style: 'decimal',
        }}
        locale={locale}
        value={
          isDefined(field.state.value) && typeof field.state.value === 'number' && !Number.isNaN(field.state.value)
            ? // must be locale-formatted (e.g. "500,2" in de/ro/pl, not "500.2") or the
              // input re-parses it on blur in the wrong locale: de reads "500.2" as
              // 5002 (dot = thousands separator) and displays "5.002"
              multipleDigitsNumberFormatter.format(field.state.value)
            : ''
        }
        onValueChange={e => {
          let value = e.valueAsNumber

          if (!isDefined(value) || Number.isNaN(value)) {
            if (allowEmpty) {
              field.handleChange(null)
              return
            }

            const fallback = Number(props.defaultValue || props.min)

            if (isDefined(fallback) && !Number.isNaN(fallback)) value = fallback
            else value = 0
          }

          if (roundToStep && isDefined(props.step) && props.step > 0) {
            value = snapToStep(value, props.step, props.min)
          }

          field.handleChange(value)
        }}
        onBlur={() => field.handleBlur()}
        {...props}
      >
        {!hideControls && <ChakraNumberInput.Control />}
        <InputGroup {...inputGroupProps}>
          <ChakraNumberInput.Input placeholder={props.placeholder} paddingRight="8" {...inputProps} />
        </InputGroup>
      </ChakraNumberInput.Root>

      {props.helpText && <Field.HelperText>{props.helpText}</Field.HelperText>}
      <ErrorMessage errorMessagePadding={props?.errorMessagePadding} message={errorMessage} />
      {description && (
        <Field.HelperText mt={2} srOnly={description}>
          {description}
        </Field.HelperText>
      )}
    </FieldRoot>
  )
}
